package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.dashboard.components.motion.StateSwap
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.features.onboarding.IntegrationsStore
import co.maytes.standupbot.dashboard.features.onboarding.WatchedReposLoadState
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.styles.Spacing
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import kotlinx.coroutines.launch

@Composable
fun GithubWatchedReposPanel(
    workspaceId: String?,
    store: IntegrationsStore,
) {
    val scope = rememberCoroutineScope()
    var saveError by remember { mutableStateOf<String?>(null) }
    var pendingSelection by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }

    LaunchedEffect(workspaceId) {
        if (workspaceId != null) store.loadGithubRepos(workspaceId)
    }

    StateSwap(state = store.watchedReposState.phase()) {
        when (store.watchedReposState) {
            WatchedReposLoadState.Idle, WatchedReposLoadState.Loading -> SettingsBars(rows = 3)
            is WatchedReposLoadState.Error -> SettingsError(what = "the watched repositories") {
                val ws = workspaceId ?: return@SettingsError
                scope.launch { store.loadGithubRepos(ws) }
            }
            WatchedReposLoadState.Ready -> {
                val watched = store.watchedRepos.toList().sorted()
                val available = store.availableRepos
                    .map { it.fullName }
                    .filter { it !in store.watchedRepos }
                    .sorted()

                if (watched.isEmpty()) {
                    SettingsEmpty(text = "No repositories picked, so every repository the install can see is scanned.")
                } else {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .styleModifier {
                                property("max-height", MAX_LIST_HEIGHT)
                                property("overflow-y", "auto")
                                property("gap", Spacing.md.toString())
                            },
                    ) {
                        watched.forEachIndexed { index, repo ->
                            SettingsRowItem(
                                title = repo,
                                first = index == 0,
                                trailing = {
                                    Button(
                                        label = "Remove",
                                        variant = ButtonVariant.Ghost,
                                        size = ButtonSize.Sm,
                                        enabled = !saving,
                                        modifier = Modifier.ariaLabel("Remove $repo"),
                                        onClick = {
                                            val ws = workspaceId ?: return@Button
                                            if (saving) return@Button
                                            scope.launch {
                                                saving = true
                                                saveError = null
                                                when (val r = store.saveWatchedRepos(ws, store.watchedRepos - repo)) {
                                                    is Result.Ok -> Unit
                                                    is Result.Err -> saveError = r.error.displayMessage
                                                }
                                                saving = false
                                            }
                                        },
                                    )
                                },
                            )
                        }
                    }
                }

                SettingsField(label = "Add a repository", error = saveError) {
                    Row(
                        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", Spacing.sm.toString()) },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Select(
                            value = pendingSelection,
                            options = available.map { SelectOption(it, it) },
                            onValueChange = { pendingSelection = it },
                            modifier = Modifier.fillMaxWidth().styleModifier { property("max-width", MAX_SELECT_WIDTH) },
                            disabled = saving || available.isEmpty(),
                            placeholder = if (available.isEmpty()) {
                                "All visible repos already watched"
                            } else {
                                "Select a repo to add…"
                            },
                            ariaLabel = "Add a repo to the watched list",
                        )
                        Button(
                            label = if (saving) "Saving…" else "Add",
                            variant = ButtonVariant.Secondary,
                            size = ButtonSize.Sm,
                            enabled = !saving && pendingSelection.isNotBlank() && workspaceId != null,
                            onClick = {
                                val ws = workspaceId ?: return@Button
                                val pick = pendingSelection.takeIf { it.isNotBlank() } ?: return@Button
                                scope.launch {
                                    saving = true
                                    saveError = null
                                    when (val r = store.saveWatchedRepos(ws, store.watchedRepos + pick)) {
                                        is Result.Ok -> pendingSelection = ""
                                        is Result.Err -> saveError = r.error.displayMessage
                                    }
                                    saving = false
                                }
                            },
                        )
                    }
                }
            }
        }
    }
}

private const val MAX_LIST_HEIGHT = "24rem"
private const val MAX_SELECT_WIDTH = "20rem"
